'use client';

import {
  SecurityScanConclusionEnum,
  SecurityScanDetectorTypeEnum,
  type InventoryResource,
} from '@repo/api-gw-sdk';

import { useDAL } from '@/contexts/useDAL';

import { DataAnomalyCard } from './dataAnomalyCard';
import { DataAnomalyCleanCard } from './dataAnomalyCleanCard';
import { SecurityScanCard } from './securityScanCard';

const DETECTOR_TYPES = [
  SecurityScanDetectorTypeEnum.RansomwareBehavior,
  SecurityScanDetectorTypeEnum.Malware,
  SecurityScanDetectorTypeEnum.DataAnomaly,
] as const;

interface DetectorCardsProps {
  resource: InventoryResource;
  onExclusionChanged: () => Promise<void>;
}

function useDetectorSummary(
  resourceId: string,
  detectorType: SecurityScanDetectorTypeEnum
) {
  const dal = useDAL();
  return dal.security.useScansSummary(resourceId, detectorType);
}

function DetectorCardWrapper({
  resource,
  detectorType,
  onExclusionChanged,
}: {
  resource: InventoryResource;
  detectorType: SecurityScanDetectorTypeEnum;
  onExclusionChanged: () => Promise<void>;
}) {
  const { data: scans, isLoading } = useDetectorSummary(
    resource.id,
    detectorType
  );

  if (!isLoading && !scans?.lastScan) return null;

  if (detectorType === SecurityScanDetectorTypeEnum.DataAnomaly) {
    if (isLoading || !scans?.lastScan) return null;
    if (scans.lastScan.conclusion === SecurityScanConclusionEnum.Clean) {
      return (
        <DataAnomalyCleanCard resource={resource} lastScan={scans.lastScan} />
      );
    }
    return <DataAnomalyCard resource={resource} scans={scans} />;
  }

  if (
    !isLoading &&
    scans?.lastScan?.conclusion === SecurityScanConclusionEnum.Clean
  )
    return null;

  return (
    <SecurityScanCard
      resource={resource}
      onExclusionChanged={onExclusionChanged}
      detectorType={detectorType}
      scans={scans}
      isLoading={isLoading}
    />
  );
}

export const DetectorCards = ({
  resource,
  onExclusionChanged,
}: DetectorCardsProps) => {
  return (
    <>
      {DETECTOR_TYPES.map((detectorType) => (
        <DetectorCardWrapper
          key={detectorType}
          resource={resource}
          detectorType={detectorType}
          onExclusionChanged={onExclusionChanged}
        />
      ))}
    </>
  );
};
